import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import {
  DollarSign, Store, ShoppingBag, AlertOctagon, Clock, TrendingUp, ArrowUpRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { adminApi } from '@/services/api';
import { formatNPRCompact } from '@/utils/format';
import { cn } from '@nepal-ecom/ui';
import { Card, CardHeader, CardTitle, CardContent } from '@nepal-ecom/ui';
import { Badge } from '@nepal-ecom/ui';
import { Skeleton } from '@nepal-ecom/ui';
import { Button } from '@nepal-ecom/ui';

type Tone = 'primary' | 'emerald' | 'amber' | 'red' | 'cobalt' | 'purple';

const TONES: Record<Tone, { iconBg: string; icon: string; ring: string }> = {
  primary: { iconBg: 'bg-primary-50', icon: 'text-primary-600', ring: 'ring-primary-100' },
  emerald: { iconBg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
  amber: { iconBg: 'bg-amber-50', icon: 'text-amber-600', ring: 'ring-amber-100' },
  red: { iconBg: 'bg-red-50', icon: 'text-red-600', ring: 'ring-red-100' },
  cobalt: { iconBg: 'bg-cobalt-50', icon: 'text-cobalt-600', ring: 'ring-cobalt-100' },
  purple: { iconBg: 'bg-purple-50', icon: 'text-purple-600', ring: 'ring-purple-100' },
};

const ACTIVITY_DOT: Record<string, string> = {
  vendor_approved: 'bg-emerald-500',
  vendor_rejected: 'bg-red-500',
  order_placed: 'bg-cobalt-500',
  dispute_raised: 'bg-amber-500',
  payout_processed: 'bg-purple-500',
};
const DEFAULT_DOT = 'bg-slate-400';

interface KpiProps {
  icon: React.ElementType;
  label: string;
  value: string;
  subtext?: string;
  delta?: { value: string; positive: boolean };
  tone: Tone;
  loading?: boolean;
}

function KpiCard({ icon: Icon, label, value, subtext, delta, tone, loading }: KpiProps) {
  const t = TONES[tone];
  return (
    <Card className="transition-all hover:shadow-card-hover hover:-translate-y-0.5">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center ring-1', t.iconBg, t.ring)}>
            <Icon className={cn('h-[18px] w-[18px]', t.icon)} />
          </div>
          {delta && (
            <Badge variant={delta.positive ? 'success' : 'destructive'} className="gap-0.5">
              <TrendingUp className={cn('h-3 w-3', !delta.positive && 'rotate-180')} />
              {delta.value}
            </Badge>
          )}
        </div>
        <div className="mt-4 space-y-1">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
          {loading ? (
            <Skeleton className="h-8 w-24 mt-1" />
          ) : (
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
          )}
          {subtext && <p className="text-xs text-slate-500">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function SalesChart({ data }: { data: Array<{ date: string; gmv: number; orders: number }> }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle>Platform GMV</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">Gross merchandise value · last 30 days</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-primary-500" />
            <span className="text-slate-600">GMV</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-secondary-500" />
            <span className="text-slate-600">Orders</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="gmvGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E11D48" stopOpacity={0.16} />
                <stop offset="100%" stopColor="#E11D48" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => format(new Date(d), 'dd MMM')}
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v) => formatNPRCompact(v)}
              tick={{ fontSize: 11, fill: '#94A3B8' }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip
              formatter={(v: number, name: string) => [
                name === 'gmv' ? formatNPRCompact(v) : v,
                name === 'gmv' ? 'GMV' : 'Orders',
              ]}
              labelFormatter={(l) => format(new Date(l), 'dd MMM yyyy')}
              contentStyle={{
                borderRadius: 10,
                fontSize: 12,
                border: '1px solid #E2E8F0',
                boxShadow: '0 12px 32px -8px rgb(15 23 42 / 0.18)',
              }}
            />
            <Area type="monotone" dataKey="gmv" stroke="#E11D48" strokeWidth={2} fill="url(#gmvGradient)" />
            <Line type="monotone" dataKey="orders" stroke="#F59E0B" strokeWidth={2} dot={false} strokeDasharray="4 4" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function VendorQueue({ vendors }: { vendors: Array<{ id: string; storeName: string; submittedAt: string }> }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-500" />
          Pending Verification
          {vendors.length > 0 && <Badge variant="warning" className="ml-1">{vendors.length}</Badge>}
        </CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/vendors?status=under_review" className="text-xs">View all <ArrowUpRight className="h-3 w-3" /></Link>
        </Button>
      </CardHeader>
      <CardContent className="pt-0 pb-0">
        {vendors.length === 0 ? (
          <div className="py-10 text-center">
            <div className="mx-auto h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center mb-2">
              <Clock className="h-5 w-5 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-slate-700">All caught up</p>
            <p className="text-xs text-slate-500 mt-0.5">No vendors awaiting review</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 -mx-5">
            {vendors.slice(0, 5).map((v) => (
              <li key={v.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{v.storeName}</p>
                  <p className="text-xs text-slate-500">
                    Submitted {format(new Date(v.submittedAt), 'dd MMM yyyy')}
                  </p>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/vendors/${v.id}/verify`}>Review</Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ActivityFeed({ events }: { events: Array<{ id: string; type: string; message: string; timestamp: string }> }) {
  const displayed = events.slice(0, 12);
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle>Platform Activity</CardTitle></CardHeader>
      <CardContent className="pt-0">
        {displayed.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
        ) : (
          <ul className="space-y-3.5 max-h-64 overflow-y-auto pr-1">
            {displayed.map((event, idx) => {
              const dotColor = ACTIVITY_DOT[event.type] ?? DEFAULT_DOT;
              return (
                <li key={event.id} className="flex items-start gap-3">
                  <div className="relative mt-1.5 flex-shrink-0">
                    <div className={cn('h-2 w-2 rounded-full', dotColor)} />
                    {idx === 0 && (
                      <div className={cn('absolute inset-0 h-2 w-2 rounded-full animate-ping opacity-60', dotColor)} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 leading-snug">{event.message}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {format(new Date(event.timestamp), 'dd MMM, hh:mm a')}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => adminApi.getDashboardStats(),
    select: (res) => res.data.data,
    refetchInterval: 60_000,
  });

  const stats = data?.stats ?? {};
  const salesChart = data?.salesChart ?? [];
  const pendingVendors = data?.pendingVendors ?? [];
  const recentActivity = data?.recentActivity ?? [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Platform Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time overview of Shishyastra marketplace.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          Live · refreshes every 60s
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={DollarSign}
          label="Total GMV"
          value={formatNPRCompact(stats.totalGmv ?? 0)}
          subtext={`Today: ${formatNPRCompact(stats.todayGmv ?? 0)}`}
          tone="emerald"
          loading={isLoading}
        />
        <KpiCard
          icon={Store}
          label="Active Vendors"
          value={String(stats.activeVendors ?? 0)}
          subtext={`${stats.pendingVendors ?? 0} pending review`}
          tone="primary"
          loading={isLoading}
        />
        <KpiCard
          icon={ShoppingBag}
          label="Orders Today"
          value={String(stats.ordersToday ?? 0)}
          subtext={`Total: ${(stats.totalOrders ?? 0).toLocaleString()}`}
          tone="cobalt"
          loading={isLoading}
        />
        <KpiCard
          icon={AlertOctagon}
          label="Open Disputes"
          value={String(stats.openDisputes ?? 0)}
          subtext="Requires attention"
          tone="red"
          loading={isLoading}
        />
      </div>

      <SalesChart data={salesChart} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <VendorQueue vendors={pendingVendors} />
        <ActivityFeed events={recentActivity} />
      </div>
    </div>
  );
}
